// src/pages/MyPage/MyTicket.jsx

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

import { sendToApp, addAppMessageListener } from "../../../bridges/appBridge";
// import useAuth from "../../../hooks/Regist/useAuth";
// import AuthDebugPanelInline from "../../AdsLogin/components/detailComponents/AuthDebugPanelInline";
import check from "../../../assets/icons/Select/check_checked.svg";
import { HeaderIconL } from "../../../assets/components/HeaderGroup";
import close from "../../../assets/icons/System/close.svg";
import { useFixedLayout } from "../../../hooks/useFixedLayout";
import radioOn from "../../../assets/icons/Select/radio_checked_color.svg";
import radioOff from "../../../assets/icons/Select/radio_unchecked.svg";
import TokenBizInfoPage from "../../../assets/components/TokenBizInfoPage";

const API = process.env.REACT_APP_FASTAPI_ADS_URL;

// 앱 메시지 기다리는 헬퍼
function waitAppMessage(filterFn, timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    const off = addAppMessageListener((msg) => {
      try {
        if (filterFn(msg)) {
          clearTimeout(timer);
          off?.();
          resolve(msg);
        }
      } catch {
        // 무시
      }
    });

    const timer = setTimeout(() => {
      off?.();
      reject(new Error(`waitAppMessage timeout ${timeoutMs}ms`));
    }, timeoutMs);
  });
}

// 숫자 → "5,500" 포맷
const fmt = (n) => (Number(n) || 0).toLocaleString("ko-KR");

const MyTicketNonTab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const origin = location.state?.origin || "home";
  const returnTo = location.state?.returnTo;
  const returnState = location.state?.returnState;
  const record = location.state?.record;
  const manualData = location.state?.manualData;
  const storeBusinessNumber = location.state?.storeBusinessNumber
    || localStorage.getItem("store_business_number");

  const { headerRef, headerSpace } = useFixedLayout();

  const [planList, setPlanList] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [isPurchasing, setIsPurchasing] = useState(false);

  // const { session, isAuthed, loading, error } = useAuth();

  // 서버로 보낼 마지막 결제 payload
  const lastPurchasePayloadRef = useRef(null);
  const purchaseHandledRef = useRef(false);

  // ===== 웹뷰 쪽에 "IAP 준비됨" 알림 =====
  useEffect(() => {
    const sendReady = () => {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({ type: "WEB_IAP_READY" })
      );
    };
    sendReady();

    window.addEventListener("pageshow", sendReady);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") sendReady();
    });

    return () => {
      window.removeEventListener("pageshow", sendReady);
    };
  }, []);

  // ===== 상품 목록 조회 (/plan/list) =====
  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API}/plan/list`);
      setPlanList(res.data || []);
    } catch (err) {
      console.error("이용권 목록 조회 실패", err);
    }
  };

  // ===== 결제 내역 조회 (/ticket/list) =====

  useEffect(() => {
    fetchPlans();
  }, []);

  // ===== 토큰(단건) 전용 플랜만 추리기 =====
  const tokenPlans = useMemo(
    () =>
      (Array.isArray(planList) ? planList : []).filter((p) => {
        const cycle = Number(p.BILLING_CYCLE);
        const type = String(p.TICKET_TYPE || "").toLowerCase();
        // BILLING_CYCLE = 0 이고, 테스트/컨시어지 등은 제외
        if (cycle !== 0) return false;
        if (["test", "start", "concierge"].includes(type)) return false;
        return true;
      }),
    [planList]
  );

  // 앱 쪽에 IAP 정보 요청
  async function requestIapInfoSimple(timeoutMs = 8000) {
    sendToApp("GET_IAPINFO");
    const msg = await waitAppMessage(
      (m) => m?.type === "IAPINFO",
      timeoutMs
    );
    return msg.payload ?? msg;
  }


  // ===== 앱에서 결제 결과 받기 =====
  useEffect(() => {
    const off = addAppMessageListener(async (msg) => {
      console.log("[APP_MSG]", msg);

      if (
        msg?.type !== "PURCHASE_RESULT" &&
        msg?.type !== "SUBSCRIPTION_RESULT"
      ) {
        return;
      }

      // 이미 한 번 처리한 결제 결과면 무시 (안드 3번 신호 방지)
      if (purchaseHandledRef.current) {
        console.log("[IAP NonTab] duplicate result ignored");
        return;
      }

      const p = msg.payload ?? msg;
      const base = lastPurchasePayloadRef.current;

      // 0) 사용자 결제창에서 직접 취소한 경우 → 서버로 안 보냄
      if (!p.success && (p.cancelled || p.error_code === "E_USER_CANCELLED")) {
        setIsPurchasing(false);
        lastPurchasePayloadRef.current = null;
        setSelectedTicketId(null);
        return;
      }

      if (!base) {
        console.error("구매 컨텍스트가 없습니다.");
        alert("결제 정보 매핑에 실패했습니다. 다시 시도해주세요.");
        setIsPurchasing(false);
        return;
      }

      // 성공/실패 공통 필드
      const commonBody = {
        ...base,
        platform: p.platform || "android",
        product_id: p.product_id || base.product_id,
        purchase_token: p.purchase_token || null,
        transaction_id: p.transaction_id || null,
      };

      // 1) 결제 실패(취소가 아님) → type = "fail" 로 DB에 기록
      if (!p.success) {
        purchaseHandledRef.current = true;

        const failBody = {
          ...commonBody,
          type: "fail",
        };

        try {
          await axios.post(`${API}/token/purchase`, failBody, {
            headers: { "Content-Type": "application/json" },
          });
          console.log("[IAP NonTab] fail logged", failBody);
        } catch (err) {
        } finally {
          setIsPurchasing(false);
          lastPurchasePayloadRef.current = null;
          setSelectedTicketId(null);
        }
        return;
      }

      // 2) 결제 성공 → 서버 검증 + 성공 시 기존대로 페이지 이동
      purchaseHandledRef.current = true;

      const successBody = {
        ...commonBody,
        type: base.type ?? "one_time",
      };

      try {
        const res = await axios.post(`${API}/token/purchase`, successBody, {
          headers: { "Content-Type": "application/json" },
        });

        if (res.data?.success) {
          // 성공 시 purchased 페이지로 이동 유지
          navigate("/myticket/purchased", {
            state: {
              token_amount: base.token_amount,
              origin,
              storeBusinessNumber,
              record,
              manualData,
              returnTo,
              returnState,
            },
          });
        } else {
          alert(
            "서버 검증 실패: " +
            (res.data?.message || "알 수 없는 오류입니다.")
          );
        }
      } catch (err) {
        console.error("결제 서버 오류 (NonTab):", err);
        alert("서버 오류로 인해 결제가 완료되지 않았습니다.");
      } finally {
        setIsPurchasing(false);
        lastPurchasePayloadRef.current = null;
        setSelectedTicketId(null);
      }
    });

    return () => off?.();
  }, [navigate, origin, storeBusinessNumber, record, manualData, returnTo, returnState]);



  // ===== 결제 버튼 클릭 =====
  const onPurchase = async (plan) => {
    if (isPurchasing) { // 중복 클릭 방지
      return;
    }

    const user_id = localStorage.getItem("user_id");
    if (!user_id) {
      alert("로그인 후 이용해주세요.");
      return;
    }

    const now = new Date();
    const planType = String(plan.TICKET_TYPE || "").toLowerCase(); // ex) "BASIC" → "basic"
    const id = Number(plan.TICKET_ID);

    let productId = null;
    if (planType === "basic") {
      if (id === 18) productId = "wm_basic_n";
      else if (id === 19) productId = "wm_standard_n";
      else if (id === 20) productId = "wm_premium_n";
    }

    if (!productId) {
      console.error("매핑 누락", { planType, ticketId: id });
      alert("인앱 상품 ID 매핑에 실패했습니다. (관리자에게 문의)");
      return;
    }

    // 서버에 보낼 원본 payload 저장
    lastPurchasePayloadRef.current = {
      user_id,
      ticket_id: id,
      payment_method: "app",
      payment_date: now.toISOString(),
      qty: 1,
      type: "one_time",
      billing_cycle: "없음",
      plan_type: planType,
      product_id: productId,
      token_amount: plan.TOKEN_AMOUNT
    };

    purchaseHandledRef.current = false;

    console.log("[PURCHASE_PAYLOAD]", lastPurchasePayloadRef.current);

    setIsPurchasing(true);

    // 앱 쪽 인앱 결제 시작
    sendToApp("START_ONE_TIME_PURCHASE", { product_id: productId });

    try {
      const info = await requestIapInfoSimple(10000);
      console.log("[IAPINFO]", info);
    } catch (e) {
      console.warn("IAPINFO timeout/error:", e);
    }
  };

  const navMyPage = () => {
    if (returnTo) {
      navigate(returnTo, { state: returnState, replace: true });
      return;
    }    

    // myCalendarDetail에서 들어온 경우 → 다시 생성하기로 복귀
    if (origin === "myCalendarDetail") {
      navigate("/mycalendar/detail", { state: { ...location.state } });
      return;
    }

    // myCalendar에서 들어온 경우 → 캘린더로 복귀
    if (origin === "myCalendar") {
      navigate("/mycalendar", { state: { ...location.state } });
      return;
    }

    if (origin === "aiResult") {
      navigate("/manual/ai/result", { state: { ...location.state } });
      return;
    }

    // 기본: 홈으로 이동
    navigate("/ads/main", {
      replace: true,
      state: {
        tab: "home",
        storeBusinessNumber
      },
    });
  };

  return (
    <div className="mx-auto h-dvh flex-col">
      <div ref={headerRef} className="fixed top-0 left-0 right-0 z-10">
        <HeaderIconL
          className=""
          title="토큰 구매"
          icon={close}
          onIconClick={navMyPage}
        />
      </div>

      {/* 헤더 높이만큼 내려온 실제 컨텐츠 영역 */}
      <div
        className="flex-1 flex flex-col"
        style={{ paddingTop: headerSpace }}
      >
        <>
          <main className="flex-1 overflow-y-auto">
            {/* 상단 카피 + 기능 리스트 */}
            <section>
              <p className="text-t2 text-text-default pt-[16px] pl-[16px]">
                토큰을 구매하고
                <br />
                더 많은 마케팅을 시작해 보세요
              </p>

              <ul className="pt-[40px] text-b1 text-text-default px-[8px]">
                <li className="flex items-start gap-[4px] p-[8px]">
                  <img src={check} alt="" />
                  <span>가게 분석 AI Report 제공</span>
                </li>
                <li className="flex items-start gap-[4px] p-[8px]">
                  <img src={check} alt="" />
                  <span>50여 종의 디자인 스타일</span>
                </li>
                <li className="flex items-start gap-[4px] p-[8px]">
                  <img src={check} alt="" />
                  <span>20여 종의 디자인 템플릿</span>
                </li>
                <li className="flex items-start gap-[4px] p-[8px]">
                  <img src={check} alt="" />
                  <span>SNS 마케팅 효과</span>
                </li>
              </ul>
            </section>

            {/* 토큰 상품 리스트 */}
            <section className="pt-[24px]">
              {tokenPlans.map((plan) => {
                const selected = plan.TICKET_ID === selectedTicketId;
                return (
                  <div key={plan.TICKET_ID} className="w-full px-[16px] pb-[12px]">
                    <button
                      type="button"
                      onClick={() => {
                        // 선택 표시 (라디오 유지)
                        setSelectedTicketId(plan.TICKET_ID);
                        // 즉시 결제 시작
                        onPurchase(plan);
                      }}
                      className={`flex w-full justify-between p-[16px] border rounded-[16px]  ${selected
                        ? "border-border-primary"
                        : "border-border-subtle"
                        }`}
                    >
                      <div className="flex items-center gap-[8px]">
                        <span className="flex items-center justify-center">
                          <img
                            src={selected ? radioOn : radioOff}
                            alt={selected ? "선택됨" : "미선택"}
                          />
                        </span>

                        <span className="pl-[16px] text-b1 text-text-subtle whitespace-nowrap">
                          토큰 {plan.TOKEN_AMOUNT}개
                        </span>
                      </div>
                      <span className="text-t4 text-text-subtle text-right">
                        {fmt(plan.TICKET_PRICE)}원
                      </span>
                    </button>
                  </div>
                );
              })}

              {tokenPlans.length === 0 && (
                <p className="py-6 text-center text-xs text-[#8C96A8]">
                  구매 가능한 토큰 상품이 없습니다.
                </p>
              )}
            </section>

            <div className="pt-[8px] mx-[16px] border-b-[1px] border-border-subtle" />

            <div className="pb-[32px]">
              <TokenBizInfoPage />
            </div>

            {/* <div className="mt-6">
              <AuthDebugPanelInline
                session={session}
                isAuthed={isAuthed}
                loading={loading}
                error={error}
              />
            </div> */}
          </main>

        </>
      </div>
    </div>
  );
};

export default MyTicketNonTab;
